import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, Button, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classnames from "classnames";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { AuthContext } from '../../../contexts/AuthContext';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import useBackwardsCompatible from "../../../hooks/useBackwardsCompatible";
import { getAllUsersFunc, updateDashboardFunc } from "../../../lib/helper";
import theme from '../../../theme';
import { Dashboard, DealOwner, RefreshDataEnum } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 500,
            height: 'fit-content',
            padding: '16px 24px',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.25rem',
    },
    dialogContent: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        padding: '16px 8px 32px 8px',
        height: '100%',
        overflowY: 'hidden',
        gap: 8,
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: 0,
        gap: 8,
    },
    label: {
        width: 85,
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
    },
    owner: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    email: {
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    emailField: {
        width: '100%',
        border: `1px solid ${theme.colors.neutral[400]}`,
        borderRadius: 8,
        padding: 8,
        "& .MuiOutlinedInput-root": {
            height: 28,
            padding: 'unset',
            "& > input, > textarea": {
                fontSize: '1rem',
                padding: 'unset',
            },
        },
    },
    disabledField: {
        "& .MuiOutlinedInput-root": {
            "& .Mui-disabled": {
                color: `${theme.colors.neutral[500]} !important`,
                '-webkit-text-fill-color': `${theme.colors.neutral[500]} !important`,
            },
        },
    },
    datePicker: {
        width: '100%',
        border: `1px solid ${theme.colors.neutral[400]}`,
        borderRadius: 8,
        padding: 8,
        "& > .MuiInputBase-root":{
            "& > input": {
                fontFamily: 'Lato',
                fontSize: '1.2rem',
                padding: 'unset',
            },
            "& fieldset": {
              border: 0,
            },
        }
    },
    unitSelectorForm: {
        width: '100%',
        border: `1px solid ${theme.colors.neutral[400]}`,
        borderRadius: 8,
        padding: 8,
        "& > .MuiInputBase-root":{
            padding: 'unset',
            "& > div": {
                fontFamily: 'Lato',
                fontSize: '1.2rem',
                padding: 'unset',
            },
            "& fieldset": {
              border: 0,
            },
        }
    },
    deleteButton: {
        width: 100,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        background: '#E0F1F1',
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        textTransform: 'none',
    },
    commonButton: {
        width: 100,
        borderRadius: 32,
        fontWeight: 'bold',
        textTransform: 'none',
    },
    cancelButton: {
        width: 100,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        fontWeight: 'bold',
        textTransform: 'none',
    },
    optionPaper: {
        background: 'white',
        border: `1px solid ${theme.colors.neutral['100']}`,
        borderRadius: 12,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
    },
    optionContainer: {
        padding: '2px 12px',
        margin: '4px 8px 4px 16px',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        transition: '0.5s ease-in-out',
        "&:hover": {
            backgroundColor: '#EFF6FF',
            boxShadow: '0px 4px 10px -1px rgba(16, 24, 40, 0.06)',
        },
        cursor: 'pointer',
    },
    optionItem: {
        fontSize: '0.9rem !important',
        fontWeight: '600 !important',
        color: `${theme.colors.neutral['800']} !important`,
    },
    disabledInputField: {
        "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
                borderWidth: '1px !important',
                borderColor: 'rgba(0, 0, 0, 0.38) !important',
            },
        },
    },
    smallInputField: {
        "& .MuiOutlinedInput-root": {
            height: 32,
            borderRadius: 16,
        },
    },
    mediumInputField: {
        "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: 24,
        },
    },
    inviteButton: {
        height: 32,
        width: 40,
        borderRadius: '16px !important',
        borderWidth: 3,
        fontSize: '14px !important',
        textTransform: 'none !important' as any,
        fontWeight: 'bold !important',
        "&:hover": {
            borderWidth: 3,
            fontWeight: 'bold !important',
        },
    },
    smallButton: {
        height: 24,
    },
    nameField: {
        minWidth: 'fit-content',
        "& .MuiOutlinedInput-root": {
            minWidth: 250,
            height: 45,
            padding: '0 8px !important',
            borderRadius: 8,
            background: '#fff',
            "& > input": {
                padding: 'unset !important',
                fontSize: '1rem',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
}));

const UpdateDealOwnerModal: React.FC<{
    isOpen: boolean,
    dashboard: Dashboard,
    onClose: () => void,
}> = ({ isOpen, dashboard, onClose }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { setDashboard } = useContext(DashboardContext);
    const { dashboardOwner } = useBackwardsCompatible();

    const [openOptions, setOpenOptions] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [owners, setOwners] = useState<DealOwner[]>([]);
    const [ownerName, setOwnerName] = useState<string|undefined >(undefined);
    const [ownerEmail, setOwnerEmail] = useState<string|undefined >(undefined);
    const [isExistingOwner, setExistingOwner] = useState<boolean>(false);
    const nameValRef = useRef<TextFieldProps>(null);
    const emailValRef = useRef<TextFieldProps>(null);

    const isSaveReady = useMemo(() => !!ownerName, [ownerName]);

    const debouncedEmail = useDebouncedCallback(email => setOwnerEmail(email.trim()), 100, { maxWait: 200 });

    const handleLookUp = useCallback((owner?: DealOwner) => {
        const searchName = nameValRef.current?.value as string;
        const lookUpOwner = owner ?? owners.find(own => own?.name?.toLowerCase() === searchName.trim().toLowerCase());

        if (!!lookUpOwner) {
            setOwnerName(lookUpOwner?.name || '');
            setOwnerEmail(lookUpOwner?.email || '');
            setExistingOwner(true);
            setOpenOptions(false);
            if (emailValRef.current)
                emailValRef.current.value = lookUpOwner.email;
        } else {
            setOwnerName(searchName);
            setOwnerEmail(undefined);
            setExistingOwner(false);
            if (emailValRef.current)
                emailValRef.current.value = '';
            if (!!searchName)
                setOpenOptions(true);
        }
    }, [owners]);

    const handleSave = useCallback(async () => {
        if (!dashboard)
            return;

        setLoading(true);

        const updatedDashboard = {
            ...dashboard,
            dealOwner: {name: ownerName, email: ownerEmail},
            userId: ownerEmail ?? dashboard.dealOwner?.email,
            refreshData: {
                ...dashboard.refreshData,
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Title,
            },
        };

        updateDashboardFunc(updatedDashboard).then(() => {
            setDashboard(updatedDashboard);
            setDashboards((prev) =>
                prev.map((d) => (d.id === updatedDashboard.id ? updatedDashboard : d))
            );
            setLoading(false);
        }).finally(onClose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboard, isExistingOwner, ownerEmail, ownerName]);

    useEffect(()=>{
        if (userGroup) {
            getAllUsersFunc(userGroup).then(users => setOwners(users.map((user: any) => ({
                name: `${user.firstName} ${user.lastName}`,
                email: user.email,
            }) as DealOwner)));
        }
    }, [userGroup]);

    useEffect(() => {
        if (!!dashboard) {
            const dealOwner = dashboardOwner(dashboard);

            if (!!dealOwner) {
                const lookUpOwner = owners.find(own =>
                    own.name?.toLowerCase() === dealOwner.name?.trim()?.toLowerCase());

                setOwnerName(lookUpOwner?.name ?? dealOwner.name ?? undefined);
                setOwnerEmail(lookUpOwner?.email ?? dealOwner.email ?? undefined);
                setExistingOwner(true);
                if (emailValRef.current)
                    emailValRef.current.value = lookUpOwner?.email ?? dealOwner.email;
            } else {
                setOwnerName('');
                setOwnerEmail(undefined);
                setExistingOwner(false);
                if (emailValRef.current)
                    emailValRef.current.value = '';
            }
        }
    }, [dashboardOwner, dashboard, owners]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                {`Update deal owner for ${dashboard.title}`}
            </DialogTitle>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogContent className={classes.dialogContent}>
                <Stack direction="column" spacing={1} alignItems="flex-start" width="100%" mt={2}>
                    <Typography className={classes.label}>{'Name:'}</Typography>
                    <ClickAwayListener onClickAway={() => setOpenOptions(false)}>
                        <Autocomplete
                            open={openOptions}
                            disableClearable
                            autoHighlight
                            includeInputInList
                            disablePortal
                            freeSolo
                            forcePopupIcon={false}
                            onChange={(_, option) => handleLookUp(option as DealOwner)}
                            value={ownerName}
                            options={owners}
                            filterOptions={(owners, { inputValue }) =>
                                owners.filter(own =>
                                    own?.name?.toLowerCase()?.includes(inputValue?.trim()?.toLowerCase()) ||
                                    own?.email?.toLowerCase()?.includes(inputValue?.trim()?.toLowerCase())
                                )
                            }
                            getOptionLabel={(option) => (typeof option === 'string' ? option : (option.name || ''))}
                            PaperComponent={(props) => (<Box {...props} className={classes.optionPaper} />)}
                            ListboxProps={{style: { maxHeight: 165, padding: 8, }}}
                            renderOption={(props: any, option) => (
                                <Box {...props} className={classes.optionContainer}>
                                    <Stack direction="row" className={classes.optionItem} spacing={1}
                                        alignItems="flex-start" justifyContent="flex-start" width="100%">
                                        <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                                        <Stack direction="column" alignItems="flex-start" justifyContent="flex-start">
                                            <Typography className={classes.owner}>
                                                {option.name}
                                            </Typography>
                                            {!!option.email && (
                                                <Typography className={classnames(classes.owner, classes.email)}>
                                                    {option.email}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            )}
                            sx={{ width: '100%', }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    className={classnames(
                                        classes.nameField,
                                    )}
                                    placeholder={'Search or invite investors...'}
                                    onChange={() => handleLookUp()}
                                    onFocus={() => setOpenOptions(!isExistingOwner || !ownerName)}
                                    inputRef={nameValRef}
                                    sx={{ width: '100%' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ color: theme.palette.primary.main }}>
                                                <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24,
                                                    fill: isExistingOwner ? theme.palette.primary.main : "rgb(163, 167, 172)" }} />
                                            </InputAdornment>
                                        )
                                }}/>
                            )}
                        />
                    </ClickAwayListener>
                </Stack>
                <Box height={32} />
                <Stack direction="column" spacing={1} alignItems="flex-start" width="100%" mb={3}>
                    <Typography className={classes.label}>{'Email:'}</Typography>
                    <TextField variant="outlined"
                        className={classnames('no-border', classes.emailField)}
                        inputProps={{ style: { height: '100%' }, className: 'no-fade' }}
                        placeholder={'Email address ...'}
                        defaultValue={ownerEmail}
                        inputRef={emailValRef}
                        onChange={(e) => debouncedEmail(e.target.value as string)}
                        rows={1}
                    />
                </Stack>
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" width="100%">
                    <Button variant="outlined"
                        className={classes.cancelButton}
                        onClick={() => onClose()}
                    > {'Cancel'} </Button>
                    <LoadingButton variant="contained"
                        className={classes.commonButton}
                        loading={loading}
                        disabled={!isSaveReady}
                        onClick={handleSave}
                    > {'Save'} </LoadingButton>
                </Stack>
            </DialogActions>
        </Dialog>
    </>);
}

export default UpdateDealOwnerModal;